import { lazy, Suspense } from "react";

import { Media } from "@/components/layout/Media";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { ExpirationState } from "./CertificationExpirationForm";
import { CertificationForm } from "./CertificationForm";
import { Expiration } from "./types";

const CertificatePreview = lazy(() =>
  import("./CertificatePreview").then((module) => ({
    default: module.CertificatePreview,
  })),
);

export function CertificationDrawer({
  title,
  workspaceName,
  workspaceLogo,
  name,
  description,
  preview,
  onNameChange,
  onDescriptionChange,
  onExpirationChange,
  expiration,
  onSubmit,
  loading,
  errorMessage,
  isOpen,
  onOpenChange,
  disabledText,
}: {
  title: string;
  workspaceName: string;
  workspaceLogo: string;
  name: string;
  description: string;
  preview: {
    name: string;
    description: string;
    expiration: Expiration;
  };
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onExpirationChange: (expiration: ExpirationState) => void;
  expiration: ExpirationState;
  onSubmit: () => void;
  loading: boolean;
  errorMessage: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabledText: string | undefined;
}) {
  return (
    <Media lessThan="sm">
      {(_finalClassName, renderChildren) => (
        <Drawer
          open={Boolean(isOpen)}
          direction={renderChildren ? "bottom" : "right"}
          onOpenChange={onOpenChange}
        >
          <DrawerContent>
            {!renderChildren && <DrawerCloseButton />}
            <div className="flex flex-col h-full justify-between">
              {renderChildren && (
                <div className="text-base font-semibold p-4 relative bg-white border-b">
                  {title}
                  <DrawerCloseButton className="absolute top-2.5 right-2.5 left-[unset] border-none" />
                </div>
              )}
              <div className="flex flex-col h-full overflow-y-auto">
                <div className="flex bg-gray-200 justify-center">
                  <div className="flex p-4 sm:p-8">
                    <div className="flex shadow-lg sm:shadow-2xl">
                      <Suspense fallback={<div>Loading...</div>}>
                        <CertificatePreview
                          name={preview.name || "Certification Name"}
                          description={preview.description}
                          companyName={workspaceName}
                          expiration={preview.expiration}
                          logoUrl={workspaceLogo}
                        />
                      </Suspense>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-8 p-6">
                  <DrawerTitle className="hidden sm:block">{title}</DrawerTitle>
                  {errorMessage && (
                    <Alert variant="destructive">
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                  )}
                  <CertificationForm
                    name={name}
                    description={description}
                    expiration={expiration}
                    onNameChange={onNameChange}
                    onDescriptionChange={onDescriptionChange}
                    onExpirationChange={onExpirationChange}
                  />
                </div>
              </div>
              <div className="flex bg-white border-t px-6 py-3 justify-end">
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={onSubmit}
                        disabled={loading || Boolean(disabledText)}
                        className="w-full"
                      >
                        {title}
                      </Button>
                    </TooltipTrigger>
                    {disabledText && (
                      <TooltipContent>{disabledText}</TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </Media>
  );
}
